import React, { useState, useCallback } from 'react';
import FileUpload from './FileUpload';
import FileList from './FileList';
import ProgressBar from './ProgressBar';
import { compressPdf } from '../services/pdfService';
import { DownloadIcon, CompressIcon, SpinnerIcon, ErrorIcon } from './icons';
import { useTranslation } from '../i18n';

type CompressionLevel = 'low' | 'medium' | 'high';

const CompressTool: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [level, setLevel] = useState<CompressionLevel>('medium');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleFileSelect = useCallback((selectedFiles: File[]) => {
    if (selectedFiles.length > 0) {
      setFile(selectedFiles[0]);
      setError(null);
      setResultUrl(null);
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
  }, []);

  const handleCompress = async () => {
    if (!file) {
      setError(t('errorSelectFileToCompress'));
      return;
    }
    setIsLoading(true);
    setIsProcessing(false);
    setProgress(0);
    setError(null);
    setResultUrl(null);
    try {
      const compressedBlob = await compressPdf(file, level, setProgress);
      setIsProcessing(true);
      const url = URL.createObjectURL(compressedBlob);
      setResultUrl(url);
    } catch (err: any) {
      setError(err.message || t('errorUnexpected'));
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  };

  const levels: { id: CompressionLevel; label: string; description: string }[] = [
    { id: 'low', label: t('compressLevelLowLabel'), description: t('compressLevelLowDescription') },
    { id: 'medium', label: t('compressLevelMediumLabel'), description: t('compressLevelMediumDescription') },
    { id: 'high', label: t('compressLevelHighLabel'), description: t('compressLevelHighDescription') },
  ];

  return (
    <div className="w-full max-w-2xl mx-auto bg-slate-800/50 rounded-2xl shadow-xl p-6 sm:p-8 border border-slate-700">
      <FileUpload onFilesSelect={handleFileSelect} multiple={false} accept=".pdf" />
      
      {file && <FileList files={[file]} onRemoveFile={handleRemoveFile} />}

      {file && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-slate-200 mb-3">{t('compressLevelTitle')}</h3>
          <div className="space-y-3">
            {levels.map(({ id, label, description }) => (
              <label key={id} className="flex items-center p-4 bg-slate-700/50 rounded-lg cursor-pointer border border-transparent hover:border-indigo-500 transition-all">
                <input
                  type="radio"
                  name="compression-level"
                  value={id}
                  checked={level === id}
                  onChange={() => setLevel(id)}
                  className="h-5 w-5 text-indigo-600 bg-slate-600 border-slate-500 focus:ring-indigo-500"
                />
                <div className="ml-4">
                  <span className="font-medium text-slate-200">{label}</span>
                  <p className="text-sm text-slate-400">{description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}
      
      {isLoading && <ProgressBar progress={progress} isProcessing={isProcessing} />}
      
      <div className="mt-6">
        <button
          onClick={handleCompress}
          disabled={isLoading || !file}
          className="w-full flex items-center justify-center gap-3 bg-indigo-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed transition-all duration-200"
        >
          {isLoading ? <SpinnerIcon /> : <CompressIcon />}
          <span>{isLoading ? (isProcessing ? t('statusProcessing') : t('statusUploading')) : t('compressButton')}</span>
        </button>
      </div>

      {error && (
         <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg flex items-center gap-3">
            <ErrorIcon/>
            <span>{error}</span>
        </div>
      )}
      {resultUrl && (
        <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 text-green-300 rounded-lg">
          <h3 className="font-semibold mb-2">{t('successTitle')}</h3>
          <p className="text-sm mb-4">{t('successCompressMessage')}</p>
          <a
            href={resultUrl}
            download={file?.name.replace(/\.pdf$/i, '_compressed.pdf') || 'compressed.pdf'}
            className="flex w-full items-center justify-center gap-3 bg-green-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-green-700 transition-all duration-200"
          >
            <DownloadIcon/>
            {t('downloadCompressedButton')}
          </a>
        </div>
      )}
    </div>
  );
};

export default CompressTool;
